import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, Clock, EyeOff, MapPinOff, ServerCog, Bell, ArrowRight, Lock, AlertTriangle,
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }),
};

const promises = [
  { icon: Clock, title: "The 24-hour delay", desc: "Nothing about your location activity — successful or failed guesses, distances, maps — is visible to a quest creator until at least 24 hours after you submit it. Enforced on our servers, not just hidden in the app." },
  { icon: EyeOff, title: "Pin or presence? We never tell.", desc: "Solve a clue by physically standing there, or by dropping a pin from your couch a thousand miles away. Both count. The creator is never told which method you used — the distinction doesn't even exist in what creators can see." },
  { icon: MapPinOff, title: "No live tracking. Ever.", desc: "No continuous GPS. No 'share my location.' No creator access to where you are right now. PinQuests only ever records the single point you chose to submit as a guess." },
  { icon: ServerCog, title: "Secrets stay on the server", desc: "Target locations are validated server-side and never sent to your device before you solve a clue. Private quests use secure, unguessable invite links." },
  { icon: Bell, title: "Notifications wait too", desc: "Creators get 'Alex unlocked Clue #5 yesterday' — never 'Alex just arrived at Clue #5.' Every location-derived notification respects the same 24-hour delay." },
  { icon: Lock, title: "Your media, protected", desc: "Photos, videos and memories inside quests are privately stored and delivered — never publicly listable or enumerable." },
];

export default function Privacy() {
  return (
    <main data-testid="privacy-page" className="pt-16">
      <section className="py-20 border-b border-[#2A3042] relative grain">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 relative z-10">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#34C759] mb-5 flex items-center gap-2">
            <ShieldCheck size={14} /> The privacy promise
          </p>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-white max-w-4xl">
            PinQuests remembers where you guessed.<br />
            <span className="text-[#9BA1B0]">It never tells anyone where you are.</span>
          </h1>
          <p className="mt-6 text-lg text-[#9BA1B0] max-w-2xl leading-relaxed">
            A location game only works if players trust it. So privacy isn't a settings
            page here — it's the architecture.
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {promises.map((p, i) => (
            <motion.div
              key={p.title}
              variants={fadeUp} custom={i % 3} initial="hidden" whileInView="show" viewport={{ once: true }}
              className="rounded-2xl bg-[#131620] border border-[#2A3042] hover:border-[#34C759] transition-colors p-8"
            >
              <div className="w-11 h-11 rounded-full bg-[#34C759]/10 border border-[#34C759]/30 flex items-center justify-center mb-5">
                <p.icon size={19} className="text-[#34C759]" />
              </div>
              <h3 className="font-heading font-semibold text-white text-lg mb-2">{p.title}</h3>
              <p className="text-sm text-[#9BA1B0] leading-relaxed">{p.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="py-20 bg-[#0b0d14] border-y border-[#2A3042]">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="rounded-2xl bg-[#131620] border border-[#2A3042] p-8 sm:p-12">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF453A] mb-6">What players see before starting</p>
            <div className="font-heading text-white text-xl font-semibold mb-4">Location & Privacy</div>
            <div className="text-[#c9cdd8] leading-relaxed space-y-4 text-[15px]">
              <p>When you submit a location guess, PinQuests records the location you submitted.</p>
              <p>The creator of this PinQuest may later see your submitted locations, including unsuccessful attempts.</p>
              <p>To protect your privacy, location activity is delayed by at least 24 hours before becoming available to the creator.</p>
              <p>You may solve eligible clues either by visiting a location or selecting a location on the map. PinQuests does not tell the creator which method you used.</p>
            </div>
            <div className="mt-8 rounded-full bg-[#FF453A] text-white text-center font-semibold py-3.5 max-w-md">
              I UNDERSTAND — START PINQUEST
            </div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="rounded-2xl border border-[#FF453A]/30 bg-[#FF453A]/5 p-8 sm:p-10 flex gap-5">
            <AlertTriangle size={22} className="text-[#FF453A] shrink-0 mt-1" />
            <div>
              <h3 className="font-heading font-semibold text-white text-lg mb-2">Safety by design</h3>
              <p className="text-sm text-[#9BA1B0] leading-relaxed">
                Public quests are reviewed and verified before anyone can play them, so hunts
                never lead people somewhere unsafe. Private quests are invite-only and
                undiscoverable. Players — and parents of young players — see clear safety
                guidance before any quest begins: never trespass, never enter unsafe areas,
                and a map pin always counts the same as being there in person.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24 text-center">
        <div className="max-w-2xl mx-auto px-5 sm:px-8">
          <h2 className="font-heading font-semibold text-3xl tracking-tight text-white">
            A game you can hand to anyone you love.
          </h2>
          <Link to="/early-access" data-testid="privacy-cta-btn" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#FF453A] hover:bg-[#FF6961] transition-colors text-white font-semibold px-8 py-4 glow-red">
            Get Early Access <ArrowRight size={17} />
          </Link>
        </div>
      </section>
    </main>
  );
}
